import React from 'react'
import Joi from 'joi'
import { t } from 'i18next'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material'
import auth_ from '../sass/auth.module.sass'
import { useDispatch, useSelector } from '../store'
import { PublicPage } from '../component/auth/PagePublic'
import { signUp } from '../store/auth/actionCreator'
import { InputPassword } from '../component/InputPassword'
import { InputDepartment } from '../component/InputDepartment'
import { Agreement } from '../component/auth/Agreement'
import {
  email,
  ErrorsState,
  password,
  passwordRepeat,
  preventEmptySpaceOnInput,
  preventEmptySpaceOnKeyDown,
  select,
  validateForm,
} from '../function/validation'
import { FormError } from '../component/FormError'
import { clearFieldError } from '../store/common/actionCreator'

const schema = Joi.object({
  email: email,
  department: select.label(t('field.label.department')),
  password: password,
  passwordRepeat: passwordRepeat,
})

export function RegisterPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { fieldErrors } = useSelector((state) => state.common)

  const [email, setEmail] = React.useState('')
  const [department, setDepartment] = React.useState('null')
  const [password, setPassword] = React.useState('')
  const [passwordRepeat, setPasswordRepeat] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorsState>(null)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    dispatch(clearFieldError)

    const error = validateForm(schema, {
      email,
      department,
      password,
      passwordRepeat,
    })

    if (error) setErrors(error)
    else {
      dispatch(signUp(email, password, department, navigate))
    }
  }

  React.useEffect(() => {
    setErrors(null)
    dispatch(clearFieldError)
  }, [dispatch, email, department, password, passwordRepeat])

  return (
    <PublicPage>
      <Grid className={auth_.title} item>
        {t('auth.register.title')}
      </Grid>

      <Grid item mt={3}>
        <Typography variant="semiBold">{t('auth.register.hint')}</Typography>
      </Grid>

      <Grid item mt={5}>
        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            <FormControl>
              <FormLabel required>{t('field.label.email')}</FormLabel>
              <OutlinedInput
                name="email"
                value={email}
                placeholder={t('field.hint.email')}
                autoFocus
                onChange={(e) => preventEmptySpaceOnInput(e, setEmail)}
                onKeyDown={preventEmptySpaceOnKeyDown}
              />
              <FormError error={fieldErrors.registerEmail || errors?.email} />
            </FormControl>
            <InputDepartment
              value={department}
              error={errors?.department}
              label={t('field.label.department')}
              required
              onChange={setDepartment}
            />

            <FormControl>
              <InputPassword
                value={password}
                error={errors?.password}
                placeholder={t('field.hint.password')}
                label={t('field.label.password')}
                required
                onInput={setPassword}
              />
            </FormControl>
            <FormControl>
              <InputPassword
                value={passwordRepeat}
                error={errors?.passwordRepeat}
                placeholder={t('field.hint.confirmPassword')}
                label={t('field.label.confirmPassword')}
                required
                onInput={setPasswordRepeat}
              />
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              size="large"
              color="primary"
            >
              {t('auth.register.submit')}
            </Button>

            <Agreement />

            <Button onClick={() => navigate('/auth')}>
              {t('auth.register.back')}
            </Button>
          </Stack>
        </form>
      </Grid>
    </PublicPage>
  )
}
